/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Alert,
  Badge,
  Chip,
  Autocomplete,
} from "@mui/material"
import { CloudOff, CloudDone, Sync, ReportProblem, Send, Public } from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema adaptado: Uso de rojos (error) para el botón de pánico
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#d32f2f" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

// Tipos de incidencias predefinidas
const TIPOS_INCIDENCIA = [
  { id: "DELEGADO_EXPULSADO", label: "Me expulsaron / No me dejan observar" },
  { id: "FALTA_MATERIAL", label: "Falta de papeletas o actas" },
  { id: "PROSELITISMO", label: "Campaña política en la fila/recinto" },
  { id: "VIOLENCIA", label: "Altercado físico o violencia" },
  { id: "MESA_CERRADA", label: "La mesa nunca se instaló" },
  { id: "FRAUDE_EVIDENTE", label: "Intento de fraude (Urnas adulteradas)" },
  { id: "OTRO", label: "Otra emergencia" },
]

const ReportarIncidenciaDelegado = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [incidenciasPendientes, setIncidenciasPendientes] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  // Estados de RLS y Seguridad
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [recintoActual, setRecintoActual] = useState("")
  const [juntasCache, setJuntasCache] = useState([])
  const [esGlobal, setEsGlobal] = useState(false)

  // Formulario
  const [tipoSeleccionado, setTipoSeleccionado] = useState("")
  const [descripcion, setDescripcion] = useState("")
  const [juntaAsociada, setJuntaAsociada] = useState(null) // Ahora es un objeto por el Autocomplete

  // ========================================================
  // 1. INICIALIZACIÓN Y SEGURIDAD RLS
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    verificarColaLocal()
    cargarDatosLogisticos()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("incidencias_offline_queue")) || []
    setIncidenciasPendientes(cola.length)
  }

  const cargarDatosLogisticos = async () => {
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getMesasYDelegados", {})

      if (res.data && res.data.success === false) {
        setErrorAcceso(res.data.message)
        return
      }

      const realesJuntas = res.data.data.juntas || []
      const recintoAsignado = res.data.recinto || ""
      const isGlobalBackend = res.data.es_global || false

      // Formateamos las juntas para el Autocomplete
      const juntasFormateadas = realesJuntas.map((j) => {
        let nombreBase = j.nombre || `Junta ${j.junta_numero}`
        if (isGlobalBackend) {
          nombreBase += ` (Recinto: ${j.reci_codigo})`
        }
        return {
          id: j.junta_codigo,
          label: nombreBase,
        }
      })

      setJuntasCache(juntasFormateadas)
      setRecintoActual(recintoAsignado)
      setEsGlobal(isGlobalBackend)

      await localforage.setItem("incidencias_juntas_cache", juntasFormateadas)
      await localforage.setItem("incidencias_recinto_cache", recintoAsignado)
      await localforage.setItem("incidencias_global_cache", isGlobalBackend)
    } catch (error) {
      console.log("Cargando logística desde caché offline...")
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const jCache = (await localforage.getItem("incidencias_juntas_cache")) || []
        const rCache = (await localforage.getItem("incidencias_recinto_cache")) || ""
        const gCache = (await localforage.getItem("incidencias_global_cache")) || false

        setJuntasCache(jCache)
        setRecintoActual(rCache)
        setEsGlobal(gCache)
      }
    }
  }

  // ========================================================
  // 2. ENVÍO DE LA ALERTA (BOTÓN DE PÁNICO)
  // ========================================================
  const handleSubmit = async () => {
    if (!tipoSeleccionado) {
      return Swal.fire("Requerido", "Debe seleccionar el tipo de emergencia.", "warning")
    }

    const payload = {
      inc_tipo: tipoSeleccionado,
      inc_descripcion: descripcion,
      junta_codigo: juntaAsociada ? juntaAsociada.id : null,
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      // GUARDADO OFFLINE
      const cola = (await localforage.getItem("incidencias_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("incidencias_offline_queue", cola)
      setIncidenciasPendientes(cola.length)

      Swal.fire({
        icon: "warning",
        title: "Alerta en Espera (Sin Señal)",
        text: "La incidencia se guardó en su celular. Acérquese a una ventana o patio para que se envíe automáticamente al Coordinador.",
      })
      limpiarFormulario()
    } else {
      // ENVÍO ONLINE
      try {
        Swal.fire({ title: "Emitiendo Alerta...", allowOutsideClick: false, didOpen: () => Swal.showLoading() })

        const res = await api.post("/ControlElectoral/recepcionIncidencia", payload)
        const data = res?.data !== undefined ? res.data : res

        if (data.success) {
          Swal.fire({
            icon: "success",
            title: "¡Alerta Enviada!",
            text: "El mando central ha sido notificado de la emergencia.",
          })
          limpiarFormulario()
        }
      } catch (error) {
        const cola = (await localforage.getItem("incidencias_offline_queue")) || []
        cola.push(payload)
        await localforage.setItem("incidencias_offline_queue", cola)
        setIncidenciasPendientes(cola.length)

        Swal.fire({ icon: "warning", title: "Red inestable", text: "Fallo al enviar. La alerta se encoló localmente." })
        limpiarFormulario()
      }
    }
  }

  const limpiarFormulario = () => {
    setTipoSeleccionado("")
    setDescripcion("")
    setJuntaAsociada(null)
  }

  // ========================================================
  // 3. SINCRONIZACIÓN MANUAL DE ALERTAS
  // ========================================================
  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Aún sin conexión", "Busque señal para emitir las alertas.", "warning")
    const cola = (await localforage.getItem("incidencias_offline_queue")) || []
    if (cola.length === 0) return Swal.fire("Todo al día", "No hay alertas pendientes de envío.", "success")

    Swal.fire({ title: "Sincronizando Alertas...", allowOutsideClick: false, didOpen: () => Swal.showLoading() })

    let colaRestante = [...cola]

    for (let i = 0; i < cola.length; i++) {
      try {
        await api.post("/ControlElectoral/recepcionIncidencia", cola[i])
        colaRestante = colaRestante.filter((_, index) => index !== i)
      } catch (err) {
        console.error("Fallo al subir alerta", err)
      }
    }

    await localforage.setItem("incidencias_offline_queue", colaRestante)
    setIncidenciasPendientes(colaRestante.length)

    if (colaRestante.length === 0) {
      Swal.fire("¡Sincronización Completa!", "Todas las alertas llegaron a la central.", "success")
    } else {
      Swal.fire("Sincronización Parcial", "Algunas alertas siguen en cola. Reintente.", "warning")
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        {/* BARRA SUPERIOR */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? (
              <CloudOff color="error" sx={{ fontSize: 32, mr: 1 }} />
            ) : (
              <CloudDone color="success" sx={{ fontSize: 32, mr: 1 }} />
            )}
            <Typography variant="subtitle1" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "OFFLINE (SIN SEÑAL)" : "CONECTADO"}
            </Typography>
          </Box>
          <Badge badgeContent={incidenciasPendientes} color="error">
            <IconButton
              onClick={sincronizarCola}
              disabled={incidenciasPendientes === 0 || isOffline}
              sx={{ bgcolor: "#ffebee" }}
            >
              <Sync color={incidenciasPendientes > 0 ? "error" : "disabled"} />
            </IconButton>
          </Badge>
        </Box>

        {isOffline && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            <b>SIN RED:</b> La alerta se guardará en su teléfono. Acérquese a una zona con señal para que el reporte
            salga.
          </Alert>
        )}

        {/* VALIDACIÓN DE ACCESO */}
        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ mt: 2, borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, borderTop: "6px solid #d32f2f", pb: 12 }}>
            <Box display="flex" alignItems="center" mb={1} color="secondary.main">
              <ReportProblem sx={{ fontSize: 36, mr: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                Reportar Emergencia
              </Typography>
            </Box>

            {/* INDICADOR DE COBERTURA */}
            {recintoActual && (
              <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
                <Typography
                  variant="subtitle2"
                  color="primary.dark"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  📍 Cobertura: {recintoActual}
                </Typography>
                {esGlobal && (
                  <Chip
                    icon={<Public fontSize="small" />}
                    label="MODO GLOBAL"
                    color="info"
                    size="small"
                    sx={{ ml: 2, fontWeight: "bold" }}
                  />
                )}
              </Box>
            )}

            <Typography variant="body2" color="textSecondary" mb={3}>
              Use esta opción solo si existe un problema logístico o de seguridad que impida el desarrollo normal del
              escrutinio.
            </Typography>

            {/* 1. TIPO DE INCIDENCIA */}
            <TextField
              select
              fullWidth
              value={tipoSeleccionado}
              onChange={(e) => setTipoSeleccionado(e.target.value)}
              label="¿Qué está sucediendo? *"
              size="large"
              sx={{ mb: 3 }}
              InputProps={{ sx: { fontWeight: "bold", color: tipoSeleccionado ? "#d32f2f" : "inherit" } }}
            >
              {TIPOS_INCIDENCIA.map((tipo) => (
                <MenuItem key={tipo.id} value={tipo.id} sx={{ py: 1.5, fontWeight: "500" }}>
                  {tipo.label}
                </MenuItem>
              ))}
            </TextField>

            {/* 2. MESA ASOCIADA (AUTOCOMPLETE PARA SOPORTAR MODO GLOBAL) */}
            {juntasCache.length > 0 && (
              <Box mb={3}>
                <Autocomplete
                  options={juntasCache}
                  getOptionLabel={(option) => option.label || ""}
                  value={juntaAsociada}
                  onChange={(event, newValue) => setJuntaAsociada(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="¿Ocurre en una mesa específica? (Opcional)"
                      placeholder="Busque la junta..."
                      variant="outlined"
                    />
                  )}
                />
                <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                  Si deja este campo vacío, se asumirá que ocurre en un área común del recinto.
                </Typography>
              </Box>
            )}

            {/* 3. DESCRIPCIÓN */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Detalles adicionales (Opcional)"
              placeholder="Ej: El delegado del partido opositor está amenazando a la presidenta de mesa..."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* BOTÓN GIGANTE FLOTANTE */}
            <Paper
              elevation={6}
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                bgcolor: "#ffffff",
                borderTop: "2px solid #ffcdd2",
                zIndex: 1000,
              }}
            >
              <Button
                variant="contained"
                color="error"
                fullWidth
                size="large"
                startIcon={isOffline ? <CloudOff /> : <Send />}
                onClick={handleSubmit}
                sx={{ py: 2, fontSize: "1.3rem", borderRadius: 2, fontWeight: "900", boxShadow: 3 }}
              >
                {isOffline ? "GUARDAR ALERTA LOCAL" : "EMITIR ALERTA AHORA"}
              </Button>
            </Paper>
          </Paper>
        )}
      </div>
    </ThemeProvider>
  )
}

export default ReportarIncidenciaDelegado
